<template>
  <div class="resource-dropdown d-flex align-items-start">
    <ValidationProvider :vid="vid" :name="$attrs.name" :rules="rules" style="flex: 1">
      <b-form-group slot-scope="{ valid, errors }" v-bind="$attrs">
        <template #label>
          <label>
            {{ $attrs.label }}
            <span v-if="$attrs.required || rules.includes('required')" class="text-danger font-bold"
              >*</span
            >
          </label>
        </template>
        <v-select
          v-model="selectedUOM"
          :options="uomOptions"
          :filterable="false"
          placeholder="Select UOM"
          :loading="loadingUOMs || loadingSelectedUOM"
          :clearable="true"
          :class="!!errors[0] ? 'is-invalid' : ''"
          @search="onSearch"
          :disabled="$attrs.disabled"
          :state="errors[0] ? false : rules && rules.includes('required') && valid ? true : null"
          v-bind="$attrs"
        >
          <template #no-options>
            <div class="text-center text-muted">No UOMs found</div>
          </template>

          <template #spinner="{ loading }">
            <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </template>

          <template #selected-option="{ id, name, abbreviation }">
            <span class="text-muted mr-1 border-right pr-1">{{ id }}</span>
            <strong>{{ name }}</strong>
            <span class="text-muted ml-1">({{ abbreviation }})</span>
          </template>

          <template #option="{ id, name, abbreviation }">
            <b-row>
              <div class="d-flex flex-column pr-1 mr-1 border-right">
                <small class="text-muted d-block">ID</small>
                <strong>{{ id }}</strong>
              </div>
              <div class="d-flex flex-column">
                <small class="text-muted d-block">NAME</small>
                <strong class="mr-1">{{ name }}</strong>
                <small class="text-muted">({{ abbreviation }})</small>
              </div>
            </b-row>
          </template>
        </v-select>

        <b-form-invalid-feedback id="inputLiveFeedback">
          {{ errors[0] ? errors[0].replace('{field}', $attrs.label) : '' }}
        </b-form-invalid-feedback>
      </b-form-group>
    </ValidationProvider>
    <create-uom-modal @submit="onUOMCreated" />
  </div>
</template>

<script>
import { BFormGroup, BFormInvalidFeedback } from 'bootstrap-vue';
import { api } from '@/utils/api';
import { BCol, BRow } from 'bootstrap-vue';
import { ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import UOMStoreModule from '@/views/apps/fbr-invoice/unit-of-measure/UOMStoreModule.js';
import CreateUOMModal from '@/views/apps/fbr-invoice/unit-of-measure/CreateUOMModal.vue';

export default {
  name: 'UOMDropdownField',
  components: {
    ValidationProvider,
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BFormInvalidFeedback,

    // Custom Components
    'create-uom-modal': CreateUOMModal,
  },
  props: {
    vid: {
      type: String,
    },
    name: {
      type: String,
      default: 'UOM',
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    rules: {
      type: [Object, String],
      default: 'required',
    },
  },
  setup(props, { emit }) {
    const UOM_STORE_MODULE_NAME = 'uom';

    // Register module
    if (!store.hasModule(UOM_STORE_MODULE_NAME))
      store.registerModule(UOM_STORE_MODULE_NAME, UOMStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(UOM_STORE_MODULE_NAME)) store.unregisterModule(UOM_STORE_MODULE_NAME);
    });

    // Reactive references
    const uomOptions = ref([]);
    const searchTerm = ref('');
    const pageNumber = ref(1);
    const loadingUOMs = ref(false);
    const loadingSelectedUOM = ref(false);
    const selectedUOM = ref(null);
    const searchTimeout = ref(null);

    // Computed properties
    const businessID = computed(() => store.state.user?.businessID);

    // Methods
    const fetchUOMs = async () => {
      loadingUOMs.value = true;
      try {
        const response = await api.get(`/InventoryUnits/GetByPagination/${businessID.value}`, {
          params: {
            searchTerm: searchTerm.value,
            pageNumber: pageNumber.value,
            pageSize: 9999,
          },
        });

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.units
        ) {
          uomOptions.value = [...response.data.data.units];
        } else {
          console.error(
            '=== UOMDropdownField options Error in paginated uom data format:',
            response.data
          );
          uomOptions.value = [];
        }
      } catch (error) {
        console.error('=== UOMDropdownField options Error fetching uoms:', error);
        uomOptions.value = [];
      } finally {
        loadingUOMs.value = false;
      }
    };

    const onUOMCreated = (uomResponse) => {
      console.log('=== UOMDropdownField onUOMCreated:', uomResponse);
      selectedUOM.value = uomResponse;
    };

    const onSearch = (searchTermValue) => {
      // Debounce search
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTerm.value = searchTermValue;

      if (!searchTermValue || searchTermValue.trim() === '') {
        searchTerm.value = '';
        fetchUOMs();
        return;
      }

      searchTimeout.value = setTimeout(() => {
        console.log('=== UOMDropdownField fetchUOMs', searchTerm.value);
        fetchUOMs();
      }, 350);
    };

    // Watchers
    watch(
      selectedUOM,
      (newVal) => {
        emit('input', newVal?.id);
      },
      { immediate: true }
    );

    // Lifecycle hooks
    onMounted(() => {
      const uomId = props.value;

      // Fetch initial data
      fetchUOMs();

      if (uomId) {
        loadingSelectedUOM.value = true;
        store
          .dispatch('uom/fetchUOM', { id: uomId })
          .then((uom) => {
            selectedUOM.value = uom;
          })
          .catch((error) => {
            console.log('error:', error);
          })
          .finally(() => {
            loadingSelectedUOM.value = false;
          });
      }
    });

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      // Reactive references
      uomOptions,
      searchTerm,
      pageNumber,
      loadingUOMs,
      loadingSelectedUOM,
      selectedUOM,
      searchTimeout,

      // Computed properties
      businessID,

      // Methods
      fetchUOMs,
      onSearch,
      onUOMCreated,
    };
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  .vs__selected {
    width: 100%;
  }

  ::v-deep {
    .vs__dropdown-menu {
      max-height: 350px;
    }
  }
}
</style>
